<template>
  <div>
    <!-- Welcome Header -->
    <div class="welcome-section mb-4">
      <b-card
        no-body
        class="bg-primary text-white"
      >
        <b-card-body class="p-4">
          <div class="row align-items-center">
            <div class="col-md-8">
              <h2 class="text-white mb-1">
                Willkommen zurück, {{ currentUser().firstName }}!
              </h2>
              <p class="text-white-50 mb-0">
                Hier ist dein Sales Dashboard mit allen wichtigen Umsatzmetriken
              </p>
            </div>
            <div class="col-md-4 text-md-right">
              <feather-icon
                icon="TrendingUpIcon"
                size="64"
                class="text-white-50"
              />
            </div>
          </div>
        </b-card-body>
      </b-card>
    </div>

    <!-- Sales Metrics Overview -->
    <b-row class="mb-0">
      <b-col
        cols="12"
        lg="3"
        class="mb-3"
      >
        <b-card
          no-body
          class="h-100"
        >
          <b-card-body class="d-flex align-items-center">
            <div class="avatar-content mr-3">
              <div class="avatar-icon">
                <feather-icon
                  icon="TrendingUpIcon"
                  size="24"
                  class="text-success"
                />
              </div>
            </div>
            <div>
              <h5 class="mb-0">
                {{ totalRevenue }}
              </h5>
              <small class="text-muted">Gesamtumsatz</small>
            </div>
          </b-card-body>
        </b-card>
      </b-col>
      <b-col
        cols="12"
        lg="3"
        class="mb-3"
      >
        <b-card
          no-body
          class="h-100"
        >
          <b-card-body class="d-flex align-items-center">
            <div class="avatar-content mr-3">
              <div class="avatar-icon">
                <feather-icon
                  icon="DollarSignIcon"
                  size="24"
                  class="text-primary"
                />
              </div>
            </div>
            <div>
              <h5 class="mb-0">
                {{ avgMonthlyRevenue }}
              </h5>
              <small class="text-muted">Ø Monatsumsatz</small>
            </div>
          </b-card-body>
        </b-card>
      </b-col>
      <b-col
        cols="12"
        lg="3"
        class="mb-3"
      >
        <b-card
          no-body
          class="h-100"
        >
          <b-card-body class="d-flex align-items-center">
            <div class="avatar-content mr-3">
              <div class="avatar-icon">
                <feather-icon
                  icon="PercentIcon"
                  size="24"
                  :class="growthClass"
                />
              </div>
            </div>
            <div>
              <h5
                class="mb-0"
                :class="growthClass"
              >
                {{ growthPercentage }}%
              </h5>
              <small class="text-muted">Wachstum</small>
            </div>
          </b-card-body>
        </b-card>
      </b-col>
      <b-col
        cols="12"
        lg="3"
        class="mb-3"
      >
        <b-card
          no-body
          class="h-100"
        >
          <b-card-body class="d-flex align-items-center">
            <div class="avatar-content mr-3">
              <div class="avatar-icon">
                <feather-icon
                  icon="UsersIcon"
                  size="24"
                  class="text-info"
                />
              </div>
            </div>
            <div>
              <h5 class="mb-0">
                {{ activeRentals }}
              </h5>
              <small class="text-muted">Aktive Mieten</small>
            </div>
          </b-card-body>
        </b-card>
      </b-col>
    </b-row>

    <!-- Sales Hints -->
    <DashboardSalesHints />

    <!-- Main Revenue Chart - Full Width -->
    <b-row class="mb-3">
      <b-col cols="12">
        <BasicDashboardRevenue />
      </b-col>
    </b-row>

    <!-- Overview Widgets from Basic Dashboard -->
    <b-row class="mb-3">
      <b-col
        cols="12"
        lg="6"
      >
        <BasicDashboardOrders />
      </b-col>
      <b-col
        cols="12"
        lg="6"
      >
        <BasicDashboardInquires />
      </b-col>
    </b-row>
    <b-row class="mb-3">
      <b-col
        cols="12"
        lg="6"
      >
        <BasicDashboardOffers />
      </b-col>
      <b-col
        cols="12"
        lg="6"
      >
        <BasicDashboardStorages />
      </b-col>
    </b-row>

    <!-- Sales Charts -->
    <RevenueBreakdownCharts />
  </div>
</template>

<script>
import { BCard, BCardBody, BRow, BCol, BButton } from 'bootstrap-vue'
import FeatherIcon from '@/@core/components/feather-icon/FeatherIcon.vue'
import businessMetrics from '@/helper/businessMetrics'
import RevenueBreakdownCharts from './RevenueBreakdownCharts.vue'
import BasicDashboardRevenue from './BasicDashboardRevenue.vue'
import BasicDashboardOrders from './BasicDashboardOrders.vue'
import BasicDashboardInquires from './BasicDashboardInquires.vue'
import BasicDashboardOffers from './BasicDashboardOffers.vue'
import BasicDashboardStorages from './BasicDashboardStorages.vue'
import DashboardSalesHints from './DashboardSalesHints.vue'

export default {
  name: 'SalesDashboard',
  components: {
    BCard,
    BCardBody,
    BRow,
    BCol,
    BButton,
    FeatherIcon,
    RevenueBreakdownCharts,
    BasicDashboardRevenue,
    BasicDashboardOrders,
    BasicDashboardInquires,
    BasicDashboardOffers,
    BasicDashboardStorages,
    DashboardSalesHints,
  },
  setup() {
    const {
      getCurrentUser,
      getRevenueTillNow,
      getOrderItemsMonthlyRevenue,
      getRentedStorages,
    } = businessMetrics()
    
    return {
      currentUser: getCurrentUser,
      getRevenueTillNow,
      getOrderItemsMonthlyRevenue,
      getRentedStorages,
    }
  },
  data() {
    return {
      totalRevenue: '0 €',
      avgMonthlyRevenue: '0 €',
      growthPercentage: 0,
      activeRentals: 0,
    }
  },
  computed: {
    growthClass() {
      if (this.growthPercentage > 0) return 'text-success'
      if (this.growthPercentage < 0) return 'text-danger'
      return 'text-muted'
    },
  },
  mounted() {
    this.loadSalesMetrics()
  },
  methods: {
    loadSalesMetrics() {
      try {
        // Get total revenue
        this.totalRevenue = this.getRevenueTillNow()
        
        // Get monthly revenue data
        const monthlyData = this.getOrderItemsMonthlyRevenue() || {}
        const revenues = Object.values(monthlyData).map(data => data?.sum || 0)
        const validRevenues = revenues.filter(r => r > 0)
        
        // Calculate average monthly revenue
        if (validRevenues.length > 0) {
          const avgRevenue = validRevenues.reduce((sum, rev) => sum + rev, 0) / validRevenues.length
          this.avgMonthlyRevenue = `${avgRevenue.toLocaleString('de-DE', { maximumFractionDigits: 0 })} €`
        }
        
        // Calculate growth percentage
        if (validRevenues.length >= 2) {
          const lastRevenue = validRevenues[validRevenues.length - 1]
          const previousRevenue = validRevenues[validRevenues.length - 2]
          this.growthPercentage = previousRevenue > 0 
            ? Math.round(((lastRevenue - previousRevenue) / previousRevenue) * 100)
            : 0
        }
        
        // Get active rentals count
        this.activeRentals = this.getRentedStorages() || 0
      } catch (error) {
        console.error('Error loading sales metrics:', error)
      }
    },
  },
}
</script>

<style scoped>
.welcome-section .bg-primary {
  background: linear-gradient(135deg, #007bff 0%, #0056b3 100%) !important;
}

.text-white-50 {
  color: rgba(255, 255, 255, 0.5) !important;
}

.dashboard-nav {
  border-bottom: 1px solid #e9ecef;
}

.dashboard-nav-buttons .btn {
  min-width: 120px;
}

.avatar-content {
  display: flex;
  align-items: center;
  justify-content: center;
}

.avatar-icon {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: rgba(0, 123, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
}

.avatar-icon .text-success {
  color: #28a745 !important;
}

.avatar-icon .text-primary {
  color: #007bff !important;
}

.avatar-icon .text-info {
  color: #17a2b8 !important;
}

@media (max-width: 768px) {
  .dashboard-nav-buttons {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  
  .dashboard-nav-buttons .btn {
    min-width: 100px;
  }
}
</style>
